/**
 * BNPL cached consent document hook (ERS-5)
 *
 * Loads the last-seen display content of the provider's DATA_PRIVACY consent document from
 * `appInfo/bnplLegalDocuments`, modeled on `usePatientAppLegal`, so the checkout can render the
 * consent checkbox before any provider call. Written by the credi2 Cloud-Function plan; may not
 * exist on first run (`unavailable`) — the checkout must degrade gracefully rather than assume it.
 */

import type { BnplLegalDocumentCache } from '@physiofit-digital/physiofit_types';
import { doc, getDoc } from 'firebase/firestore';
import { useEffect, useState } from 'react';
import { db } from '../../../firebaseConfig';

export type BnplLegalDocumentResult =
  | { status: 'loading' }
  | { status: 'unavailable' }
  | { status: 'ready'; document: BnplLegalDocumentCache };

const bnplLegalDocumentRef = () => doc(db, 'appInfo', 'bnplLegalDocuments');

/**
 * Loads the cached BNPL provider consent document once on mount.
 *
 * @return {Object} Loading, unavailable, or ready result.
 */
export function useBnplLegalDocument(): BnplLegalDocumentResult {
  const [result, setResult] = useState<BnplLegalDocumentResult>({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      try {
        const snap = await getDoc(bnplLegalDocumentRef());
        if (cancelled) {
          return;
        }
        if (!snap.exists()) {
          setResult({ status: 'unavailable' });
          return;
        }
        const document = snap.data() as BnplLegalDocumentCache;
        if (!document.consentText?.trim()) {
          setResult({ status: 'unavailable' });
          return;
        }
        setResult({ status: 'ready', document });
      } catch (error) {
        if (!cancelled) {
          // Same reason as `useBnplCohort`: a denied read must not be indistinguishable from an
          // absent document.
          console.error('[onboarding] appInfo/bnplLegalDocuments read failed; consent unavailable', error);
          setResult({ status: 'unavailable' });
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, []);

  return result;
}
